import moment from 'moment';
import {
  getNotDeletedOrNullCondition,
  getPublishedRetiredCondition,
} from 'utils/queryHelper';

import { IDonationCampaign } from 'models/donationCampaign';
import { IFAQ } from 'models/faq';
import { Stack9SortValue } from 'models/stack9SearchBodyFormat';

import { EntityService } from './entityService';
import HttpRequestor, { BaseResponse } from './httpRequestor';

export class DonationCampaignPageService extends EntityService<IDonationCampaign> {
  constructor(httpRequestor: HttpRequestor) {
    super(httpRequestor, 'donation_campaign');
  }

  getOneByPageName(
    page: string,
    options?: any,
  ): Promise<BaseResponse<IDonationCampaign>> {
    return this.httpRequestor.get(`/${this.entity}/${page}`, options);
  }

  async getAllFromSpecificDate(
    currentDate: moment.Moment,
    additionalCondition?: any,
  ) {
    const response = await this.search({
      $where: {
        ...getPublishedRetiredCondition(currentDate),
        _is_deleted: false,
        ...(additionalCondition || {}),
      },
    });
    response.data = response.data.map(
      (donationCampaign: IDonationCampaign) => ({
        ...donationCampaign,
        raised_amount:
          parseFloat((donationCampaign.raised_amount || 0).toString()) +
          parseFloat((donationCampaign.offline_raised_amount || 0).toString()),
      }),
    );
    return response;
  }

  async getAllFromSpecificDateWithoutHiddenCampaigns(
    currentDate: moment.Moment,
  ) {
    return this.getAllFromSpecificDate(currentDate, {
      hidden_ind: false,
    });
  }

  /*
   * Partial query to get the Features FAQs
   * */
  async getAllFeaturedFAQsBySlugAndDate(
    slug: string,
    currentDate: moment.Moment,
  ): Promise<IFAQ[]> {
    const donationCampaignWithFAQs = await this.searchOne({
      $withRelated: ['featured_faqs'],
      $where: {
        slug,
        $and: [
          getPublishedRetiredCondition(currentDate, 'featured_faqs.'),
          getNotDeletedOrNullCondition('featured_faqs'),
        ],
        _is_deleted: false,
      },
      $sort: {
        'featured_faqs.sequence': Stack9SortValue.asc,
        'featured_faqs.featured_ind': Stack9SortValue.desc,
      },
    });
    return donationCampaignWithFAQs
      ? (donationCampaignWithFAQs.featured_faqs as IFAQ[])
      : [];
  }

  /*
   * Get the donation Campaign with all related values according to the published_date and retired_date
   * */
  async getDonationCampaignWithRelatedBySlugAndDate(
    slug: string,
    currentDate: moment.Moment,
  ): Promise<IDonationCampaign | null> {
    const [donationCampaign, featured_faqs] = await Promise.all([
      this.searchOne({
        $withRelated: [
          'donation_options',
          'donation_funds',
          'campaign_contents.colour_schema',
          'campaign_contents.layout',
          'campaign_contents.links',
          'donation_campaign_type',
        ],
        $where: {
          slug,
          _is_deleted: false,
          ...getPublishedRetiredCondition(currentDate),
          $and: [
            getNotDeletedOrNullCondition('donation_options'),
            getNotDeletedOrNullCondition('donation_funds'),
            getNotDeletedOrNullCondition('campaign_contents'),
            getNotDeletedOrNullCondition('campaign_contents:layout'),
            getNotDeletedOrNullCondition('campaign_contents:links'),
          ],
        },
        $sort: {
          'donation_options.sequence': Stack9SortValue.asc,
          'donation_funds.sequence': Stack9SortValue.asc,
          'campaign_contents.sequence': Stack9SortValue.asc,
        },
      }),
      this.getAllFeaturedFAQsBySlugAndDate(slug, currentDate),
    ]);

    if (!donationCampaign) {
      return null;
    }
    return {
      ...donationCampaign,
      raised_amount:
        parseFloat((donationCampaign.raised_amount || 0).toString()) +
        parseFloat((donationCampaign.offline_raised_amount || 0).toString()),
      featured_faqs,
    };
  }
}
